import { createEffect, createSignal, onCleanup } from "solid-js";

// A reader who has turned off motion at the OS level shouldn't get an
// unexpected scroll or fade sprung on them by a live list.
export const prefersReducedMotion = (): boolean =>
  typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;

type FollowNewOptions = {
  // Which edge of the container the newest row sits at: a list that appends at
  // the bottom (the summary) follows the bottom edge; a newest-first list
  // (Activity) follows the top.
  edge: "top" | "bottom";
  container: () => HTMLElement | undefined;
  rowAttr: string;
  ids: () => string[];
  animate: () => boolean;
  resetKey: () => string;
};

type FollowNew = {
  onScroll: () => void;
  showNew: () => boolean;
  jumpToNewest: () => void;
  // Re-derive the pin from where the reader now sits. A filter or facet swap
  // changes the list under a fixed scroll position with no scroll event of its
  // own, so a pin left over from a previous, shorter list would pull the reader
  // along on the next arrival even though they never asked to follow.
  recomputePin: () => void;
};

const FOLLOW_GAP = 40;

/**
 * Follow new rows as they stream into a live list: a reader watching the
 * newest is carried along, while one who has scrolled away is left in place
 * and offered a pill to jump to what arrived. Gated on `animate`, the flag
 * that already marks the live streaming context — a settled or read-only list
 * never grows, so the effect never fires there.
 *
 * Positions are read from a row's rect, never the container's scrollHeight: a
 * consumer can pad the scroll box, and that padding is not a new row.
 */
export function createFollowNew(opts: FollowNewOptions): FollowNew {
  let pinned = true;
  let following = false;
  let followTimer: ReturnType<typeof setTimeout> | undefined;
  let prevIds: Set<string> | null = null;
  let prevKey: string | null = null;
  const [showNew, setShowNew] = createSignal(false);

  // Keyed by reading the attribute back rather than a value selector: an
  // Activity row's key is a NUL-separated finding key, and CSS.escape rewrites
  // NUL to U+FFFD, so a value selector would never find the row it names. Built
  // once per lookup so targetRow and the arrival gate share a single DOM pass —
  // a filtered panel with the newest ids off the page would otherwise re-scan
  // the whole list once per id on every scroll event.
  const rowMap = (): Map<string, HTMLElement> => {
    const rows = new Map<string, HTMLElement>();
    const container = opts.container();
    if (!container) return rows;
    for (const row of container.querySelectorAll<HTMLElement>(`[${opts.rowAttr}]`)) {
      const key = row.getAttribute(opts.rowAttr);
      if (key !== null) rows.set(key, row);
    }
    return rows;
  };
  // The newest *rendered* row: walk from whichever end new rows land on —
  // appended at the bottom for the summary, prepended at the top for a
  // newest-first list like Activity — and take the first id that still has a
  // row. `ids` is the unfiltered list, so its outright newest can be off the
  // page (a facet, a search term, a panel promoted to one kind); the row to
  // follow is then the newest one still shown, not nothing. The map is keyed by
  // attribute, so section grouping placing that row mid-DOM is no obstacle.
  const pickTarget = (rows: Map<string, HTMLElement>): HTMLElement | undefined => {
    const ids = opts.ids();
    const ordered = opts.edge === "bottom" ? ids.toReversed() : ids;
    for (const id of ordered) {
      const row = rows.get(id);
      if (row) return row;
    }
    return undefined;
  };
  const targetRow = (): HTMLElement | undefined => pickTarget(rowMap());
  // "Pinned" means the newest row itself is in view, not that some row sits at
  // a container edge — the newest row isn't always the extreme one once rows
  // are grouped into sections.
  const rowVisible = (row: HTMLElement | undefined): boolean => {
    const container = opts.container();
    if (!row || !container) return false;
    const c = container.getBoundingClientRect();
    const r = row.getBoundingClientRect();
    // A row taller than the scroll box can never sit fully inside it, so for one
    // that overflows the followed edge being reached is enough. Summary rows are
    // unclamped and expand inline, so a tall newest row in a short panel is
    // reachable; requiring the far edge too would latch the pin off and bring
    // the pill back on every arrival, including right after the reader clicks it.
    const overflows = r.bottom - r.top > c.bottom - c.top;
    return opts.edge === "bottom"
      ? r.bottom <= c.bottom + FOLLOW_GAP && (overflows || r.top >= c.top)
      : r.top >= c.top - FOLLOW_GAP && (overflows || r.bottom <= c.bottom);
  };
  const scrollTo = (row: HTMLElement | undefined): void => {
    const container = opts.container();
    if (!row || !container) return;
    const c = container.getBoundingClientRect();
    const r = row.getBoundingClientRect();
    const delta =
      r.top < c.top ? r.top - c.top - FOLLOW_GAP : r.bottom > c.bottom ? r.bottom - c.bottom + FOLLOW_GAP : 0;
    if (delta === 0) return;
    // Mark the frames this scroll emits so onScroll doesn't read them as the
    // reader leaving the edge and raise the pill over an active follow.
    following = true;
    clearTimeout(followTimer);
    // Re-read the pin when the follow ends: a reader who scrolled away mid-follow
    // had those frames ignored, so a stale pin would pull them back on the next arrival.
    followTimer = setTimeout(() => {
      following = false;
      onScroll();
    }, 400);
    container.scrollTo({
      top: container.scrollTop + delta,
      behavior: prefersReducedMotion() ? "auto" : "smooth",
    });
  };
  const onScroll = (): void => {
    if (following) return;
    pinned = rowVisible(targetRow());
    if (pinned) setShowNew(false);
  };
  onCleanup(() => clearTimeout(followTimer));

  createEffect(() => {
    if (!opts.animate()) return;
    const key = opts.resetKey();
    const ids = opts.ids();
    const next = new Set(ids);
    const prev = prevIds;
    const rebaseline = key !== prevKey;
    prevIds = next;
    prevKey = key;
    // A new session (or the first run) is not rows arriving: drop any stale
    // pill and start the reader at the top. Re-derive the pin from where that
    // leaves them once laid out — a list short enough to fit is still "at the
    // newest", a tall one is not — so a pin never carries over from the
    // previous session, nor a mount default over a list that already overflows.
    if (rebaseline) {
      setShowNew(false);
      requestAnimationFrame(() => {
        opts.container()?.scrollTo({ top: 0 });
        pinned = rowVisible(targetRow());
      });
      return;
    }
    // The first list after an empty one is restored data loading, not rows
    // arriving: a new session resets while the list is empty and the resync
    // fills it a moment later, and every row would otherwise count as new.
    // Re-derive the pin from where that leaves the reader.
    if (prev === null || prev.size === 0) {
      requestAnimationFrame(() => (pinned = rowVisible(targetRow())));
      return;
    }
    const arrived = ids.filter((id) => !prev.has(id));
    if (arrived.length === 0) return;
    requestAnimationFrame(() => {
      const rows = rowMap();
      const target = pickTarget(rows);
      // A filter, a collapsed section, or a promoted panel can keep the
      // newest row off the page: that gives the reader nothing to jump to
      // and no reason for the pill.
      if (!target) return;
      // The pill answers an arrival the reader can reach. When every id that
      // arrived is filtered out, the target falls back to a row already on
      // screen and already read, so raising it would jump them nowhere new.
      if (!arrived.some((id) => rows.has(id))) return;
      if (pinned) {
        scrollTo(target);
        return;
      }
      setShowNew(true);
    });
  });

  return {
    onScroll,
    showNew,
    jumpToNewest(): void {
      setShowNew(false);
      scrollTo(targetRow());
    },
    recomputePin(): void {
      requestAnimationFrame(() => (pinned = rowVisible(targetRow())));
    },
  };
}
